import React, { useState } from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { injectIntl } from '../../util/reactIntl';

import css from './FieldNumber.module.css';

const decrement = 'decrement';
const increment = 'increment';

const getIconClasses = props => {
  const { className, disabled } = props;
  const classes = classNames(className, css.iconContainer, { [css.disabled]: disabled });
  const iconClassName = classNames(css.icon, { [css.disabled]: disabled });

  return {
    classes,
    iconClassName,
  };
};

const IconMinus = props => {
  const { classes, iconClassName } = getIconClasses(props);

  return (
    <svg
      className={classes}
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="none"
    >
      <rect x="0.5" y="0.5" width="39" height="39" rx="6" />
      <path
        className={iconClassName}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.5 20C11.5 19.4477 11.9477 19 12.5 19H27.5C28.0523 19 28.5 19.4477 28.5 20C28.5 20.5523 28.0523 21 27.5 21H12.5C11.9477 21 11.5 20.5523 11.5 20Z"
      />
    </svg>
  );
};

const IconPlus = props => {
  const { classes, iconClassName } = getIconClasses(props);

  return (
    <svg
      className={classes}
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="none"
    >
      <rect x="0.5" y="0.5" width="39" height="39" rx="6" />
      <path
        className={iconClassName}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20 11.5C20.5523 11.5 21 11.9477 21 12.5V19.0001H27.5C28.0523 19.0001 28.5 19.4478 28.5 20.0001C28.5 20.5524 28.0523 21.0001 27.5 21.0001H21V27.5C21 28.0523 20.5523 28.5 20 28.5C19.4477 28.5 19 28.0523 19 27.5V21.0001H12.5C11.9477 21.0001 11.5 20.5524 11.5 20.0001C11.5 19.4478 11.9477 19.0001 12.5 19.0001H19V12.5C19 11.9477 19.4477 11.5 20 11.5Z"
      />
    </svg>
  );
};

const NumberInputComponent = props => {
  const { value: rawValue, onChange } = props.input;
  const { initialValue, minValue = 0, maxValue, svgClassName, editable, inputId, intl } = props;
  const value =
    rawValue && rawValue >= minValue
      ? Number.parseInt(rawValue)
      : initialValue && initialValue >= minValue
      ? Number.parseInt(initialValue)
      : minValue;

  // While the field is focused the user may be mid-typing (including a
  // transiently empty box), so the raw keystrokes are held locally and only
  // committed to the form once they form a valid, in-range number.
  const [draft, setDraft] = useState(null);

  const clamp = n => {
    const withMin = Math.max(n, minValue);
    return typeof maxValue === 'number' ? Math.min(withMin, maxValue) : withMin;
  };

  const handleValueChange = event => {
    const { name } = event.target;
    if (name === increment) {
      onChange(clamp(value + 1));
    } else if (name === decrement) {
      onChange(clamp(value - 1));
    }
  };

  const handleTyping = event => {
    const next = event.target.value;
    setDraft(next);
    // Only push digits through; an empty or partial entry waits for blur.
    const parsed = Number.parseInt(next, 10);
    if (Number.isFinite(parsed) && String(parsed) === next.trim() && parsed === clamp(parsed)) {
      onChange(parsed);
    }
  };

  // On blur, settle whatever is in the box: clamp it, or fall back to the last
  // good value if it was left empty or non-numeric.
  const handleBlur = () => {
    if (draft !== null) {
      const parsed = Number.parseInt(draft, 10);
      onChange(Number.isFinite(parsed) ? clamp(parsed) : value);
      setDraft(null);
    }
  };

  return (
    <span
      className={classNames(css.numberInputWrapper, {
        [css.numberInputWrapperEditable]: editable,
      })}
    >
      <button
        className={css.numberButton}
        name={decrement}
        type="button"
        onClick={handleValueChange}
        disabled={value <= minValue}
        title={intl.formatMessage({ id: 'NumberInput.decrementButton' })}
        aria-label={intl.formatMessage({ id: 'NumberInput.decrementButton' })}
      >
        <IconMinus disabled={value <= minValue} className={svgClassName} />
      </button>
      {editable ? (
        <input
          id={inputId}
          className={classNames(css.numberInput, css.numberInputEditable)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft !== null ? draft : value}
          onChange={handleTyping}
          onBlur={handleBlur}
          aria-label={intl.formatMessage({ id: 'NumberInput.valueLabel' })}
        />
      ) : (
        <span className={css.numberInput}>{value}</span>
      )}
      <button
        className={css.numberButton}
        name={increment}
        type="button"
        onClick={handleValueChange}
        disabled={value >= maxValue}
        aria-label={intl.formatMessage({ id: 'NumberInput.incrementButton' })}
      >
        <IconPlus disabled={value >= maxValue} className={svgClassName} />
      </button>
    </span>
  );
};

const NumberInput = injectIntl(NumberInputComponent);

/**
 * Renders a numeric selector with - and + icons
 * @component
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.name
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string?} props.svgClassName
 * @param {string?} props.textClassName
 * @param {string?} props.label
 * @param {number?} props.maxValue
 * @param {number?} props.minValue
 * @param {boolean?} props.editable - render the value as a typable input instead of static text
 * @param {string?} props.inputId - id for the editable input (so a label can point at it)
 * @returns {JSX.Element} containing a numeric selector field that can be used in a form
 */
const FieldNumberComponent = props => {
  const { rootClassName, className, textClassName, id, name, label, editable, inputId } = props;

  const classes = classNames(rootClassName || css.root, className);
  // Point the label at the real input when there is one, so clicking it focuses
  // the field rather than the non-interactive wrapper.
  const labelFor = editable && inputId ? inputId : id;

  return (
    <span className={classes}>
      {label ? (
        <label htmlFor={labelFor} className={textClassName}>
          {label}
        </label>
      ) : null}
      <Field name={name}>
        {fieldRenderProps => {
          return (
            <div>
              <NumberInput {...props} {...fieldRenderProps} />
            </div>
          );
        }}
      </Field>
    </span>
  );
};

export default FieldNumberComponent;
